import React, { ReactNode, useEffect, useRef, JSX, forwardRef, useImperativeHandle, useState } from 'react';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import 'markmap-toolbar/dist/style.css';
import { ReloadOutlined } from '@ant-design/icons';
import './style.scss';
import AIMarks from '../../AiMark';
import { Display } from '@/components/Display/Display';
import html2canvas from 'html2canvas';
import MyIcon from '@/components/MyIcon';
import { v4 as uuidv4 } from 'uuid';
import * as ReactDOM from 'react-dom/client';
import { Tooltip } from 'antd';
type Props = {
    content: string;
    toolbarSlot?: JSX.Element | ReactNode;
    showOperator?: boolean;
    className?: string;
    rootClassName?: string;
    zoom?: boolean;
    autoFit?: boolean;
    maxScale?: number;
    minScale?: number;
    inModal?: boolean;
    setOpen?: (open: boolean) => void;
    size?: number;
    afterDownload?: () => void;
};
type MarkMapRef = {
    markMapObj?: Markmap | null;
    zoomPlus?: () => void;
    zoomMinus?: () => void;
};
const MarkMap = (
    { content, className = '', rootClassName = '', inModal = false, setOpen, size, afterDownload }: Props,
    ref: React.Ref<MarkMapRef>
) => {
    const refSvg = useRef<SVGSVGElement>(null);
    const refMm = useRef<Markmap>(null);
    let tempInstance = useRef<Markmap>(null);
    const transformer = new Transformer();
    const mapBoxRef = useRef<HTMLDivElement>(null);
    const [isDownLoad, setIsDownLoad] = useState(false);
    useEffect(() => {
        if (!refSvg.current) return;
        // Update data for markMap once value is changed
        if (!refMm.current) {
            const mm = Markmap.create(refSvg.current, { duration: 0.5 });
            //@ts-ignore
            refMm.current = mm;
        }
    }, [refSvg.current]);
    useEffect(() => {
        if (!refMm.current || !content) return;
        const { root } = transformer.transform(content);
        // refMm.current?.setData(root);
        refMm.current?.setData(root).then(() => {
            refMm.current?.fit();
        });
    }, [refMm.current, content]);

    useEffect(() => {
        return () => {
            refMm.current?.destroy();
            //@ts-ignore
            refMm.current = null;
            // 清空 SVG 元素内容，避免残留 DOM
            if (refSvg.current) {
                refSvg.current.innerHTML = ''; //手动清空 SVG 内部元素
            }
        };
    }, []);

    // 下载逻辑：操作隐藏的副本，不影响原始导图
    const handleDownload = async () => {
        setIsDownLoad(true);
        const tempContainer = document.createElement('div');
        let temBoxId = `tem-map-box-${uuidv4()}`;
        let temSvgId = `tem-svg-${uuidv4()}`;
        const ele = (
            <div className="web-tem-map-box" id={temBoxId}>
                <svg className="web-tem-svg" id={temSvgId} />
                <AIMarks index={1} size={24} style={{ color: '#BFBFBF', textAlign: 'right', alignSelf: 'flex-end', width: 'fit-content' }} />
            </div>
        );
        ReactDOM.createRoot(tempContainer).render(ele);

        document.body.appendChild(tempContainer);
        setTimeout(async () => {
            try {
                let svgContainer = document.getElementById(temSvgId);
                //@ts-ignore
                tempInstance.current = Markmap.create(svgContainer);
                const temTransformer = new Transformer();
                const { root } = temTransformer.transform(content);
                tempInstance.current?.setData(root).then(() => {
                    tempInstance.current?.fit();
                });
                await new Promise((resolve) => setTimeout(resolve, 800));
                let canvasContainer = document.getElementById(temBoxId);
                // 4. 对副本截图（此时原始导图完全无变化）
                const canvas = await html2canvas(canvasContainer as any, {
                    useCORS: true,
                    scale: 3, // 高清缩放
                    logging: false
                });
                canvas.toBlob((blob) => {
                    const link = document.createElement('a');
                    link.download = `IMG_${uuidv4()}.png`; // 文件名;
                    link.href = URL.createObjectURL(blob as any);
                    link.click();
                    URL.revokeObjectURL(link.href);
                }, 'image/png');
                // 5. 下载图片
                // const link = document.createElement('a');
                // link.download = `IMG_${uuidv4()}.png`; // 文件名;
                // link.href = canvas.toDataURL('image/png');
                // link.click();
            } finally {
                // 6. 无论成功失败，都销毁临时容器（避免内存泄漏）
                tempInstance.current?.destroy();
                // @ts-ignore
                tempInstance.current = null;
                // 清空 SVG 元素内容，避免残留 DOM
                document.body.removeChild(tempContainer);
                setIsDownLoad(false);
                if (afterDownload) afterDownload();
            }
        }, 500);
    };
    // //下载
    const download = async () => {
        handleDownload();
    };
    useImperativeHandle(ref, () => {
        return {
            markMapObj: refMm.current,
            download,
            isDownLoad,
            refMm: refMm
        };
    });
    return (
        <div className={'mind-map-user' + (' ' + rootClassName || '')}>
            <Display show={!inModal}>
                <div className={'mind-map-user-toolbar'}>
                    {isDownLoad ? (
                        <ReloadOutlined spin />
                    ) : (
                        <Tooltip title="下载">
                            <span className="action-icon download" onClick={download}>
                                <MyIcon type="icon-xiazai" />
                            </span>
                        </Tooltip>
                    )}

                    <Tooltip title="全屏查看">
                        <span
                            onClick={() => {
                                if (setOpen) setOpen(true);
                            }}
                            className="action-icon"
                        >
                            <MyIcon type="icon-zhankai" />
                        </span>
                    </Tooltip>
                </div>
            </Display>
            <div className="map-box" ref={mapBoxRef}>
                <svg className={'flex-1' + (' ' + className || '')} ref={refSvg} />
                <AIMarks index={1} size={size} style={{ color: '#BFBFBF', textAlign: 'right' }} />
            </div>
        </div>
    );
};
export default forwardRef(MarkMap);
